import { AxiosError } from "axios";
import { z } from "zod";

export class NotImplementedError extends Error {}

const CLIENT_ERROR_MIN_STATUS = 400;
const SERVER_ERROR_MIN_STATUS = 500;

/** A message worth showing: anything but a non-empty string resolves to `undefined`. */
const displayableMessage = z.string().trim().min(1).optional().catch(undefined);

/**
 * JSON error bodies the platform API returns on a failed request. Most endpoints return
 * `{ message }` (orion's `ApiErrorMessage`); validation-style endpoints — e.g. project
 * create/update — return `{ errors: [{ msg }] }`.
 */
const apiErrorBodySchema = z
  .object({
    message: displayableMessage,
    errors: z
      .array(z.object({ msg: displayableMessage }).catch({ msg: undefined }))
      .optional()
      .catch(undefined),
  })
  .catch({ message: undefined, errors: undefined });

type ApiErrorBody = z.output<typeof apiErrorBodySchema>;

function isClientError(
  error: unknown,
): error is AxiosError & { response: NonNullable<AxiosError["response"]> } {
  return (
    error instanceof AxiosError &&
    !!error.response &&
    error.response.status >= CLIENT_ERROR_MIN_STATUS &&
    error.response.status < SERVER_ERROR_MIN_STATUS
  );
}

/**
 * Every place a message might live, in precedence order: validation entries first (they are the
 * more specific explanation), then the top-level message. Matches how the project create/update
 * dialogs already resolve the two fields.
 */
function messageCandidates({
  message,
  errors,
}: ApiErrorBody): (string | undefined)[] {
  return (
    errors?.map((entry) => {
      return entry.msg;
    }) ?? []
  ).concat(message);
}

/**
 * Extract a message that is safe to show the user from a failed request's error.
 *
 * Only 4xx responses carry a user-actionable reason the backend intends to surface (e.g. a
 * Training Set that mixes ingredient-quantity dimensions returns 400 with an explanation). 5xx
 * and non-HTTP errors return `undefined` so callers fall back to a generic message and internal
 * details are never leaked to the user.
 */
export function getUserFacingErrorMessage(error: unknown): string | undefined {
  if (!isClientError(error)) {
    return undefined;
  }

  return messageCandidates(apiErrorBodySchema.parse(error.response.data)).find(
    Boolean,
  );
}
